<script setup lang="ts">
import type {
  IUserSelection,
  IViewData,
} from "@/stories/form/DatePicker/shared";
import ViewHeader from "@/stories/form/DatePicker/ViewHeader.vue";
import { toUnix } from "@/stories/utils/dates";

const CURRENT_YEAR = new Date().getFullYear();

interface Props {
  view: IViewData;
  selection: IUserSelection | null;
  minDate?: number;
  maxDate?: number;
}

const props = defineProps<Props>();

defineEmits(["setView"]);

/**
 * This function creates a year arrays with 12 years by the `props.view.page`.
 *
 * If the `props.view.page` is 2023, the result would be: [2019-2030]
 *
 * It starts the 2019 because we want to show the current year (2023) in the
 * center of the second row.
 */
const getYearsByPage = () => {
  return new Array(12)
    .fill(0)
    .map((item: any, index: any) => props.view.page + index - 4);
};

const isSelectedYear = (year: any) => {
  return props.selection?.year === year;
};

const isCurrentYear = (year: any) => {
  return year === CURRENT_YEAR;
};

const isYearOutOfRange = (year: number): boolean => {
  const { minDate, maxDate } = props;
  if (!minDate && !maxDate) return false;

  // Check if entire year is out of range
  const firstDayOfYear = toUnix(new Date(year, 0, 1, 0, 0, 0));
  const lastDayOfYear = toUnix(new Date(year, 11, 31, 23, 59, 59));

  // Year is out of range if it's completely before minDate or completely after maxDate
  if (minDate && lastDayOfYear < minDate) return true;
  if (maxDate && firstDayOfYear > maxDate) return true;

  return false;
};
</script>

<template>
  <div>
    <ViewHeader
      @prev-click="$emit('setView', { page: props.view.page - 12 })"
      @next-click="$emit('setView', { page: props.view.page + 12 })"
      @title-click="$emit('setView', { type: 'years' })"
    >
      {{ props.view.page - 4 }} - {{ props.view.page + 7 }}
    </ViewHeader>

    <div class="grid grid-cols-3 gap-2">
      <button
        type="button"
        v-for="(year, index) of getYearsByPage()"
        :key="index"
        class="text-year-button"
        :class="[
          'w-full py-3 text-center rounded-lg text-neutral1000 text-lg font-semibold focus-visible-strong',
          isCurrentYear(year) && !isSelectedYear(year)
            ? 'bg-neutral50 text-darkOcean500 font-bold'
            : '',
          isSelectedYear(year)
            ? 'bg-darkOcean100 border border-darkOcean500 focus-visible:border-transparent'
            : '',
          { 'opacity-40': isYearOutOfRange(year) },
        ]"
        @click="$emit('setView', { year, type: 'months' })"
      >
        {{ year }}
      </button>
    </div>
  </div>
</template>
